import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit'
import { CartItem, CartStats } from '../../types/cart'
import {
  getCartItemUnitPrice,
  hasCartItemDiscount,
  roundCurrency,
} from '../../utils/cartUtils'

interface CartState {
  items: CartItem[]
  isOpen: boolean
  addedItemNotification: {
    item: CartItem | null
    show: boolean
  }
}

// Функція для завантаження корзини з localStorage
const loadCartFromStorage = (): CartItem[] => {
  try {
    const cartData = localStorage.getItem('cart')
    return cartData ? JSON.parse(cartData) : []
  } catch (error) {
    // Помилка завантаження корзини з localStorage
    return []
  }
}

// Функція для збереження корзини в localStorage
const saveCartToStorage = (items: CartItem[]): void => {
  try {
    localStorage.setItem('cart', JSON.stringify(items))
  } catch (error) {
    // Помилка збереження корзини в localStorage
  }
}

// Функція для генерації унікального ключа товару в корзині
const getCartItemKey = (item: CartItem): string => {
  const grindType = item.meta_data?.find(m => m.key === 'grindType')?.value || 'default'
  return `${item.id}-${item.variationId || 'default'}-${item.chosenVariantId || 'default'}-${grindType}`
}

// Функція для обчислення статистики корзини
const calculateCartStats = (items: CartItem[]): CartStats => {
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0)
  const totalPrice = roundCurrency(items.reduce((sum, item) => {
    return sum + getCartItemUnitPrice(item) * item.quantity
  }, 0))
  const totalSavings = roundCurrency(items.reduce((sum, item) => {
    if (hasCartItemDiscount(item)) {
      return sum + (item.regular_price - getCartItemUnitPrice(item)) * item.quantity
    }
    return sum
  }, 0))

  return { totalItems, totalPrice, totalSavings }
}

const initialState: CartState = {
  items: loadCartFromStorage(),
  isOpen: false,
  addedItemNotification: {
    item: null,
    show: false,
  },
}

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    addToCart: (state, action: PayloadAction<CartItem>) => {
      const newItemKey = getCartItemKey(action.payload)
      const existingItem = state.items.find(item => getCartItemKey(item) === newItemKey)
      
      if (existingItem) {
        existingItem.quantity += action.payload.quantity
      } else {
        state.items.push(action.payload)
      }
      saveCartToStorage(state.items)

      // Показуємо повідомлення про додавання
      state.addedItemNotification = {
        item: action.payload,
        show: true,
      }
    },
    updateQuantity: (
      state,
      action: PayloadAction<{ id: number; variationId?: number; grindType?: string; chosenVariantId?: string; quantity: number }>,
    ) => {
      const { id, variationId, grindType, chosenVariantId, quantity } = action.payload
      const targetKey = `${id}-${variationId || 'default'}-${chosenVariantId || 'default'}-${grindType || 'default'}`
      const item = state.items.find(item => getCartItemKey(item) === targetKey)
      
      if (item) {
        item.quantity = quantity
        saveCartToStorage(state.items)
      }
    },
    removeFromCart: (state, action: PayloadAction<CartItem>) => {
      const targetKey = getCartItemKey(action.payload)
      state.items = state.items.filter(item => getCartItemKey(item) !== targetKey)
      saveCartToStorage(state.items)
    },
    clearCart: state => {
      state.items = []
      saveCartToStorage(state.items)
    },
    toggleCart: state => {
      state.isOpen = !state.isOpen
    },
    openCart: state => {
      state.isOpen = true
    },
    closeCart: state => {
      state.isOpen = false
    },
    // Додаємо товар з певною кількістю
    addItemWithQuantity: (state, action: PayloadAction<{ item: CartItem; quantity: number }>) => {
      const { item, quantity } = action.payload
      const newItemKey = getCartItemKey(item)
      const existingItem = state.items.find(existing => getCartItemKey(existing) === newItemKey)
      
      if (existingItem) {
        existingItem.quantity += quantity
      } else {
        state.items.push({ ...item, quantity })
      }
      saveCartToStorage(state.items)
    },
    // Оновлюємо кількість товару на певну дельту
    updateQuantityByDelta: (
      state,
      action: PayloadAction<{ id: number; variationId?: number; grindType?: string; chosenVariantId?: string; delta: number }>,
    ) => {
      const { id, variationId, grindType, chosenVariantId, delta } = action.payload
      const targetKey = `${id}-${variationId || 'default'}-${chosenVariantId || 'default'}-${grindType || 'default'}`
      const item = state.items.find(existing => getCartItemKey(existing) === targetKey)
      
      if (item) {
        const newQuantity = item.quantity + delta
        if (newQuantity <= 0) {
          state.items = state.items.filter(existing => getCartItemKey(existing) !== targetKey)
        } else {
          item.quantity = newQuantity
        }
        saveCartToStorage(state.items)
      }
    },
    // Actions для управління повідомленням про додавання товару
    showAddedItemNotification: (state, action: PayloadAction<CartItem>) => {
      state.addedItemNotification = {
        item: action.payload,
        show: true,
      }
    },
    hideAddedItemNotification: state => {
      state.addedItemNotification = {
        item: null,
        show: false,
      }
    },
  },
})

// Селектори
export const selectCartItems = (state: { cart: CartState }) => state.cart.items
export const selectCartIsOpen = (state: { cart: CartState }) => state.cart.isOpen

// Memoized selector for cart stats to prevent unnecessary re-renders
export const selectCartStats = createSelector(
  [(state: { cart: CartState }) => state.cart.items],
  (items) => calculateCartStats(items)
)

export const selectCartItemCount = (state: { cart: CartState }) =>
  state.cart.items.reduce((sum, item) => sum + item.quantity, 0)
export const selectAddedItemNotification = (state: { cart: CartState }) =>
  state.cart.addedItemNotification

export const {
  addToCart,
  updateQuantity,
  removeFromCart,
  clearCart,
  toggleCart,
  openCart,
  closeCart,
  addItemWithQuantity,
  updateQuantityByDelta,
  showAddedItemNotification,
  hideAddedItemNotification,
} = cartSlice.actions

export default cartSlice.reducer
